// The Change step of a list area (lane B10 S20a; plan D14; CR-066 "Change -> Validate -> Version -> Activate -> Audit"):
// the typed editor of one reason list, taxonomy or status-label set. Status labels relabel the fixed statuses of their
// machine (which status follows which stays code; tone and order are kept as published). A reason or a taxonomy value is
// relabelled, retired with *Active* off — never removed once published, so a record that stamped an earlier version
// keeps resolving its word — or added; a reason may require a note. An entry added in this change may be taken out
// again before it is proposed. The editor is controlled: it hands every edit back as a whole new body and writes
// nothing; Validate (`config.validate`) and Version (`config.propose`) are the Journey's.
//
// The body helpers below are pure (the node suite runs them); they keep every field they do not edit (`external_ref`,
// `parent`, `tone`, `order`) exactly as it was.

import { useEffect, useId, useState } from "react";
import {
  ACTION_LABELS, Button, COMMON_LABELS, EmptyState, Flex, IconButton, Input, Stack, Table, Td, Th, Toggle, Tr, VisuallyHidden,
} from "@/mc";
import { CONSOLE_LABELS, keyWord } from "./labels";
import { hsKey } from "@/views/b10/lib/hsKey";

export type Body = Record<string, unknown>;
export type ListField = "reasons" | "items" | "labels";

/** The body field a list kind keeps its entries in; null for a kind that is not a list. */
export function listField(kind: string): ListField | null {
  if (kind === "reasons") return "reasons";
  if (kind === "taxonomies") return "items";
  if (kind === "status_labels") return "labels";
  return null;
}

/** One entry as the editor and the table draw it. */
export interface ListEntry {
  slug: string;
  /** His word; null for a status shown without one yet. */
  label: string | null;
  /** Offered for new records (a retired value stays readable for the records that used it). */
  active: boolean;
  /** A reason that needs a note when it is chosen. */
  requiresNote: boolean;
}

type Raw = Record<string, unknown>;
const isRaw = (value: unknown): value is Raw => typeof value === "object" && value !== null && !Array.isArray(value);

/** The entries of a list body, in its published order (status labels by their `order`, then by status). */
export function entriesOf(kind: string, body: Body | null | undefined): ListEntry[] {
  const field = listField(kind);
  if (!field || !body) return [];
  if (field === "labels") {
    const labels: Raw = isRaw(body.labels) ? body.labels : {};
    return Object.entries(labels)
      .map(([slug, entry]): { slug: string; entry: Raw } => ({ slug, entry: isRaw(entry) ? entry : {} }))
      .sort((a, b) => Number(a.entry.order ?? 0) - Number(b.entry.order ?? 0) || a.slug.localeCompare(b.slug))
      .map(({ slug, entry }) => ({ slug, label: typeof entry.label === "string" ? entry.label : null, active: true, requiresNote: false }));
  }
  const list = Array.isArray(body[field]) ? (body[field] as unknown[]) : [];
  return list.filter(isRaw).map((entry) => ({
    slug: String(entry.slug),
    label: typeof entry.label === "string" ? entry.label : null,
    active: entry.active !== false,
    requiresNote: entry.requires_note === true,
  }));
}

/** A list with no entry yet: the body a first version starts from. */
export function emptyBody(kind: string): Body {
  const field = listField(kind);
  return field === "labels" ? { labels: {} } : field ? { [field]: [] } : {};
}

/** A copy of a body, deep enough that an edit never reaches the published version it started from. */
export function cloneBody(body: Body | null | undefined): Body {
  return body ? (JSON.parse(JSON.stringify(body)) as Body) : {};
}

export interface EntryPatch {
  label?: string | null | undefined;
  active?: boolean | undefined;
  requiresNote?: boolean | undefined;
}

/** The body with one entry changed. A status label set to "" is shown without a word (null), as published. */
export function setEntry(kind: string, body: Body, slug: string, patch: EntryPatch): Body {
  const field = listField(kind);
  const next = cloneBody(body);
  if (!field) return next;
  if (field === "labels") {
    const labels: Raw = isRaw(next.labels) ? next.labels : {};
    const found = labels[slug];
    const entry: Raw = isRaw(found) ? found : {};
    if (patch.label !== undefined) entry.label = patch.label === null || patch.label === "" ? null : patch.label;
    labels[slug] = entry;
    next.labels = labels;
    return next;
  }
  const list = Array.isArray(next[field]) ? (next[field] as unknown[]) : [];
  next[field] = list.map((entry) => {
    if (!isRaw(entry) || entry.slug !== slug) return entry;
    const out: Raw = { ...entry };
    if (patch.label !== undefined) out.label = patch.label ?? "";
    if (patch.active !== undefined) {
      if (patch.active) delete out.active;
      else out.active = false;
    }
    if (patch.requiresNote !== undefined && field === "reasons") {
      if (patch.requiresNote) out.requires_note = true;
      else delete out.requires_note;
    }
    return out;
  });
  return next;
}

/** A slug for a new entry from its words: lower case, words joined by `_`, starting with a letter, unique, at most `max`
 * characters (the column the slug is stored in; B1's `REASON_SLUG_MAX`, 24 for the floored lists, 64 otherwise). */
export function slugFor(label: string, taken: ReadonlySet<string>, max = 64): string {
  const base = label
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^(\d)/, "v_$1")
    .slice(0, max)
    .replace(/_+$/g, "") || "value";
  let slug = base;
  for (let n = 2; taken.has(slug); n += 1) {
    const suffix = `_${n}`;
    slug = `${base.slice(0, max - suffix.length).replace(/_+$/g, "")}${suffix}`;
  }
  return slug;
}

/** The body with a new entry at the end (reason lists and taxonomies; a status machine's statuses are code). */
export function addEntry(kind: string, body: Body, label: string, max = 64): { body: Body; slug: string | null } {
  const field = listField(kind);
  const words = label.trim();
  if (!field || field === "labels" || !words) return { body: cloneBody(body), slug: null };
  const next = cloneBody(body);
  const list = Array.isArray(next[field]) ? (next[field] as unknown[]) : [];
  const slug = slugFor(words, new Set(entriesOf(kind, next).map((e) => e.slug)), max);
  next[field] = [...list, { slug, label: words }];
  return { body: next, slug };
}

/** The body without an entry. The editor offers it only for an entry this change added: a published value is retired. */
export function removeEntry(kind: string, body: Body, slug: string): Body {
  const field = listField(kind);
  const next = cloneBody(body);
  if (!field || field === "labels") return next;
  const list = Array.isArray(next[field]) ? (next[field] as unknown[]) : [];
  next[field] = list.filter((entry) => !(isRaw(entry) && entry.slug === slug));
  return next;
}

/** One entry's state in words: its word, *Active*, and *Notes: Required* for a reason that needs a note. */
export function entrySummary(kind: string, entry: ListEntry | undefined): string | null {
  if (!entry) return null;
  const parts = [entry.label ?? entry.slug];
  if (listField(kind) !== "labels" && entry.active) parts.push(CONSOLE_LABELS.active);
  if (entry.requiresNote) parts.push(`${CONSOLE_LABELS.notes}: ${COMMON_LABELS.required}`);
  return parts.join(" · ");
}

export interface EntryChange {
  slug: string;
  /** The entry's word now (its earlier word when it no longer has one). */
  name: string;
  old: string | null;
  new: string | null;
}

/** The change between two bodies, entry by entry (Old → New), in the new body's order; unchanged entries left out. */
export function entryChanges(kind: string, before: Body | null | undefined, after: Body | null | undefined): EntryChange[] {
  const was = new Map(entriesOf(kind, before).map((e) => [e.slug, e]));
  const now = entriesOf(kind, after);
  const slugs = [...now.map((e) => e.slug), ...[...was.keys()].filter((slug) => !now.some((e) => e.slug === slug))];
  const out: EntryChange[] = [];
  for (const slug of slugs) {
    const a = was.get(slug);
    const b = now.find((e) => e.slug === slug);
    const old = entrySummary(kind, a);
    const next = entrySummary(kind, b);
    if (old !== next) out.push({ slug, name: b?.label ?? a?.label ?? slug, old, new: next });
  }
  return out;
}

export interface ListEditorProps {
  kind: string;
  configKey: string;
  /** The body being changed. */
  value: Body;
  /** Every edit, as the whole new body. */
  onChange: (body: Body) => void;
  /** The version in effect: its values are published (retired, never removed). */
  published?: Body | null | undefined;
  /** The entry whose *Edit* opened the change: it takes focus first. */
  focus?: string | undefined;
  /** The longest slug the list's record stores (default 64). */
  slugMax?: number | undefined;
}

export function ListEditor({ kind, configKey, value, onChange, published, focus, slugMax }: ListEditorProps) {
  const field = listField(kind);
  const word = keyWord(configKey) ?? configKey;
  const entries = entriesOf(kind, value);
  const publishedSlugs = new Set(entriesOf(kind, published).map((e) => e.slug));
  const [words, setWords] = useState("");
  const prefix = useId();
  const inputId = (slug: string) => `${prefix}-e-${slug}`;

  useEffect(() => {
    if (!focus) return undefined;
    // after the slide-over has placed its own first focus
    const frame = window.requestAnimationFrame(() => document.getElementById(`${prefix}-e-${focus}`)?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [focus, prefix]);

  if (!field) return null;
  const add = () => {
    const out = addEntry(kind, value, words, slugMax);
    if (out.slug === null) return;
    onChange(out.body);
    setWords("");
  };
  const lists = field !== "labels";
  return (
    <Stack gap={12}>
      <Table
        dense
        label={word}
        cols={field === "reasons" ? 4 : lists ? 3 : 1}
        empty={entries.length === 0 ? <EmptyState inline>{word}</EmptyState> : undefined}
        head={(
          <Tr>
            <Th>{lists ? word : CONSOLE_LABELS.status}</Th>
            {lists ? <Th>{CONSOLE_LABELS.active}</Th> : null}
            {field === "reasons" ? <Th>{CONSOLE_LABELS.notes}</Th> : null}
            {lists ? <Th align="r"><VisuallyHidden>{ACTION_LABELS.cancel}</VisuallyHidden></Th> : null}
          </Tr>
        )}
      >
        {entries.map((entry) => (
          <Tr key={entry.slug}>
            <Td label={lists ? word : CONSOLE_LABELS.status} sub={<span className="mc-mono">{entry.slug}</span>}>
              <Input
                sm
                id={inputId(entry.slug)}
                value={entry.label ?? ""}
                aria-label={entry.label ?? entry.slug}
                onChange={(event) => onChange(setEntry(kind, value, entry.slug, { label: event.target.value }))}
                hs={hsKey("cfg.entry", entry.slug)}
              />
            </Td>
            {lists ? (
              <Td label={CONSOLE_LABELS.active}>
                <Toggle
                  label={CONSOLE_LABELS.active}
                  hideLabel
                  checked={entry.active}
                  onChange={(on) => onChange(setEntry(kind, value, entry.slug, { active: on }))}
                  hs={hsKey("cfg.active", entry.slug)}
                />
              </Td>
            ) : null}
            {field === "reasons" ? (
              <Td label={CONSOLE_LABELS.notes}>
                <Toggle
                  label={COMMON_LABELS.required}
                  checked={entry.requiresNote}
                  onChange={(on) => onChange(setEntry(kind, value, entry.slug, { requiresNote: on }))}
                  hs={hsKey("cfg.note", entry.slug)}
                />
              </Td>
            ) : null}
            {lists ? (
              <Td align="r">
                {publishedSlugs.has(entry.slug) ? null : (
                  <IconButton
                    sm
                    icon="x"
                    label={ACTION_LABELS.cancel}
                    onClick={() => onChange(removeEntry(kind, value, entry.slug))}
                    hs={hsKey("cfg.remove", entry.slug)}
                  />
                )}
              </Td>
            ) : null}
          </Tr>
        ))}
      </Table>
      {lists ? (
        <Flex gap={8} wrap>
          <Input
            sm
            value={words}
            aria-label={word}
            onChange={(event) => setWords(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                add();
              }
            }}
            
          />
          <Button
            sm
            icon="plus"
            onClick={add}
            blocked={words.trim() ? undefined : { reason: COMMON_LABELS.required }}
            
          >
            {CONSOLE_LABELS.add}
          </Button>
        </Flex>
      ) : null}
    </Stack>
  );
}
